import { QueryKeys } from "@/constants/Query";
import { useApi } from "@/hooks/useApi";
import { useApiMutation } from "@/hooks/useApiMutation";
import axiosInstance from "@/lib/axios";
import { CareTeam, getIdentifierForSystem } from "@curai/js-fhir-tools";
import { useQueryClient } from "@tanstack/react-query";

export interface ExternalProvider {
  firstName: string;
  lastName: string;
  credential: string;
  npi: string;
  city: string;
  state: string;
}

export interface SpecialtyCode {
  code: string;
  display: string;
  system: string;
}

export interface ExternalCareTeamMember {
  nameFullFormatted: string;
  npi?: string;
  specialtyCode: SpecialtyCode;
  addedAt?: string;
}

export interface ExternalCareTeam {
  careTeamId: string;
  members: ExternalCareTeamMember[];
}

export interface ExternalCareTeamResponse {
  careteam: CareTeam;
}

export interface AddExternalCareTeamMemberRequest {
  careTeamId?: string;
  members: ExternalCareTeamMember[];
}

export interface PostExternalCareTeamMemberRequest {
  members: ExternalCareTeamMember[];
}

export interface PatchExternalCareTeamMemberRequest {
  careTeamId: string;
  members: ExternalCareTeamMember[];
}

export interface AddExternalCareTeamMemberParams {
  careTeamId?: string;
  members: ExternalCareTeamMember[];
}

export interface ExternalProviderSearchResponse {
  results: ExternalProvider[];
}

export interface RemoveExternalCareTeamMemberRequest {
  members: {
    npi?: string;
    nameFullFormatted?: string;
  }[];
}

export interface RemoveExternalCareTeamMemberParams {
  careTeamId: string;
  npi?: string;
  name?: string;
}

export interface UpdateExternalCareTeamMemberRequest {
  careTeamId: string;
  existingMember: ExternalCareTeamMember;
  newMember: ExternalCareTeamMember;
}

export interface UpdateExternalCareTeamMemberParams {
  existingMember: ExternalCareTeamMember;
  newMember: ExternalCareTeamMember;
}

export const useExternalCareTeam = (patientId?: string) => {
  return useApi<ExternalCareTeamResponse, ExternalCareTeam>({
    queryOptions: {
      enabled: Boolean(patientId),
      queryKey:
        QueryKeys.Patient.MedicalRecords.ExternalCareTeam.DEFAULT(patientId),
      queryFn: async () => {
        const response = await axiosInstance.get(
          "/charting/external-careteam/",
          {
            params: { patientId },
          },
        );
        return response?.data?.careteam;
      },
      select: (data): ExternalCareTeam => {
        const careTeam = new CareTeam(data);
        return {
          careTeamId: careTeam.id || "",
          members:
            careTeam.participant
              ?.filter((participant) => !participant.period?.end)
              .map((participant) => {
                const role = participant.role?.[0];

                return {
                  nameFullFormatted: participant.member?.display || "",
                  npi: getIdentifierForSystem(
                    participant.member,
                    "http://hl7.org/fhir/sid/us-npi",
                  ),
                  specialtyCode: {
                    code: role?.coding?.[0]?.code || "",
                    display: role?.coding?.[0]?.display || role?.text || "",
                    system: role?.coding?.[0]?.system || "",
                  },
                  addedAt: participant.period?.start,
                };
              }) || [],
        };
      },
    },
  });
};

export const useAddExternalCareTeamMember = ({
  patientId,
  onSuccess,
}: {
  patientId?: string;
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();

  const postCareTeam = async (data: PostExternalCareTeamMemberRequest) => {
    const response = await axiosInstance.post("/charting/external-careteam/", {
      patientId: patientId,
      members: data.members,
    });
    return response.data;
  };

  const patchCareTeam = async (data: PatchExternalCareTeamMemberRequest) => {
    const response = await axiosInstance.patch(
      `/charting/external-careteam/${data.careTeamId}/members/add`,
      {
        members: data.members,
      },
    );
    return response.data;
  };

  return useApiMutation<
    ExternalCareTeamResponse,
    AddExternalCareTeamMemberParams
  >({
    mutationOptions: {
      mutationKey: ["patient", "external-care-team", "add-member"],
      mutationFn: async (data) => {
        if (data.careTeamId) {
          return await patchCareTeam({
            careTeamId: data.careTeamId,
            members: data.members,
          });
        }
        return await postCareTeam({
          members: data.members,
        });
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.ExternalCareTeam.DEFAULT(
              patientId,
            ),
        });
        onSuccess?.();
      },
    },
  });
};

export const useSearchExternalProviders = (
  searchTerm: string,
  state?: string,
) => {
  const enabled =
    searchTerm?.trim().length > 2 && (!state || state.length === 2);
  const params = {
    name: searchTerm,
    state,
  };
  return useApi({
    queryOptions: {
      enabled,
      queryFn: async () => {
        const response =
          await axiosInstance.get<ExternalProviderSearchResponse>(
            "/charting/external-careteam/members/search-external",
            { params },
          );
        return response.data;
      },
      queryKey:
        QueryKeys.Patient.MedicalRecords.ExternalCareTeam.PROVIDER_SEARCH({
          searchTerm,
          state,
        }),
    },
  });
};

export const useRemoveExternalCareTeamMember = (patientId?: string) => {
  const queryClient = useQueryClient();

  const removeCareTeamMember = async (
    careTeamId: string,
    data: RemoveExternalCareTeamMemberRequest,
  ) => {
    await axiosInstance.patch(
      `/charting/external-careteam/${careTeamId}/members/remove`,
      data,
    );
  };

  return useApiMutation({
    mutationOptions: {
      mutationKey: ["patient", "external-care-team", "remove-member"],
      mutationFn: async (data: RemoveExternalCareTeamMemberParams) => {
        const payload = {
          members: [
            {
              npi: data.npi || undefined,
              nameFullFormatted: data.npi ? undefined : data.name,
            },
          ],
        };
        await removeCareTeamMember(data.careTeamId, payload);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.ExternalCareTeam.DEFAULT(
              patientId,
            ),
        });
      },
    },
  });
};

export const useUpdateExternalCareTeamMember = ({
  patientId,
  onSuccess,
}: {
  patientId?: string;
  onSuccess?: () => void;
}) => {
  const queryClient = useQueryClient();

  const updateCareTeamMember = async (
    careTeamId: string,
    data: UpdateExternalCareTeamMemberParams,
  ) => {
    await axiosInstance.patch(
      `/charting/external-careteam/${careTeamId}/members/update`,
      data,
    );
  };

  return useApiMutation({
    mutationOptions: {
      mutationKey: ["patient", "external-care-team", "update-member"],
      mutationFn: async (data: UpdateExternalCareTeamMemberRequest) => {
        const payload = {
          existingMember: data.existingMember,
          newMember: data.newMember,
        };
        await updateCareTeamMember(data.careTeamId, payload);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey:
            QueryKeys.Patient.MedicalRecords.ExternalCareTeam.DEFAULT(
              patientId,
            ),
        });
        onSuccess?.();
      },
    },
  });
};
